import cn from 'classnames'
import { ComponentType, ComponentPropsWithoutRef, forwardRef, memo, useEffect, useMemo } from 'react'
import { v4 as uuid } from 'uuid'

import styles from './Checkbox.module.scss'

import useCombinedRefs from '~hooks/useCombinedRefs'
import CheckIcon from '~icons/check.svg'
import MinusIcon from '~icons/minus.svg'
import { stopPropagation } from '~lib/helpers'

export interface CheckboxProps extends Omit<ComponentPropsWithoutRef<'input'>, 'size' | 'type'> {
  inputId?: string
  label?: string
  description?: string
  variant?: 'highlightBlur' | 'highlightLight' | 'lavender'
  silent?: boolean
  size?: 's' | 'm'
  wrapped?: boolean
  active?: boolean
  indeterminate?: boolean
  icon?: SvgrComponent | ComponentType<React.PropsWithChildren<React.PropsWithChildren<Record<string, any>>>>
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      id,
      inputId,
      silent,
      size = 'm',
      variant = 'highlightBlur',
      className,
      disabled,
      active,
      wrapped,
      label,
      description,
      indeterminate = false,
      children,
      icon: Icon = CheckIcon,
      ...props
    },
    ref
  ) => {
    const htmlFor = useMemo(() => inputId || `${uuid()}-checkbox`, [inputId])

    const combinedRef = useCombinedRefs<HTMLInputElement>(ref)

    useEffect(() => {
      if (combinedRef?.current) {
        combinedRef.current.indeterminate = indeterminate
      }
    }, [indeterminate, combinedRef])

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
      <div
        id={id}
        className={cn(
          styles.root,
          styles[size],
          styles[variant],
          {
            [styles.silent]: silent,
            [styles.disabled]: disabled,
            [styles.active]: active,
            [styles.wrapped]: wrapped,
          },
          className
        )}
        onClick={stopPropagation}
      >
        <input ref={combinedRef} id={htmlFor} type="checkbox" hidden tabIndex={-1} disabled={disabled} {...props} />
        <label htmlFor={htmlFor} className={styles.container}>
          <span className={styles.checkbox}>
            <Icon className={styles.checkIcon} />
            <MinusIcon className={styles.indeterminateIcon} />
          </span>
          {children ||
            ((label || description) && (
              <span className={styles.note}>
                {label && <span className={styles.label}>{label}</span>}
                {description && <span className={styles.description}>{description}</span>}
              </span>
            ))}
        </label>
      </div>
    )
  }
)

export default memo(Checkbox)
